import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Col, Form, Input, Row, Flex, Upload, message, Popover, Select, Space } from "antd";

import Container from "@/components/container/Container";
import CardComponent from "@/components/card";
import ButtonComponent from "@/components/button/Button";
import BackWithLabel from "@/components/common/BackWithLabel";

import useTriggerNotification from "@/hooks/useTriggerNotification";
import { getAllSkills } from "@/services/common/getAllSkills";
import { getAllInterests } from "@/services/common/getAllInterests";
import { getAllLanguages } from "@/services/common/getAllLanguages";
import IconCross from "@/components/icons/IconCross";
import "@/styles/pages/guide/guide-profile.scss";
import { getUserInfo } from "@/reducers/commonSlice";
import { useSelector } from "react-redux";
import { getGuideProfile } from "@/services/guides/getGuideProfile";
import IconUploadDocs from "@/components/icons/IconUploadDocs";
import { updateGuideProfile } from "@/services/guides/updateGuideProfile";
import { uploadFile } from "@/services/users/uploadFile";
import { updateFile } from "@/services/users/updateFile";
import dayjs from "dayjs";
import { getMissingFields, openLinkInNewTab } from "@/helpers";
import IconUploadV2 from "@/components/icons/IconUploadV2";
import IconDots from "@/components/icons/IconDots";
import useUnsavedChanges from "@/hooks/useUnsavedChanges";
import IconShare from "@/components/icons/IconShare";
import Toaster from "@/components/toaster/Toaster";

const { Dragger } = Upload;
const formItemLayoutWithOutLabel = {
  wrapperCol: { xs: { span: 24 }, sm: { span: 24 } },
};
const customizeRequiredMark = (label, { required }) => (
  <>
    {label}
    {required && <span style={{ color: "red" }}>*</span>}
  </>
);

const GuideProfileDetail = () => {
  const [form] = Form.useForm();
  const [videoForm] = Form.useForm();
  const { trigger, element } = useTriggerNotification();

  const [isFormDisabled, setIsFormDisabled] = useState(true);
  const [isVideoFormDisabled, setIsVideoFormDisabled] = useState(true);
  const [isVideoUpdating, setIsVideoUpdating] = useState(false);

  const [skillsList, setSkillsList] = useState([]);
  const [intrestList, setIntrestList] = useState([]);
  const [launguageList, setLaunguageList] = useState([]);

  const [thumbnailFileList, setThumbnailFilelist] = useState([]);
  const [videoFileList, setVideoFileList] = useState([]);

  const [isProfileIncomplete, setIsProfileIncomplete] = useState(false);
  const [guideProfileData, setGuideProfileData] = useState({});
  const userInfo = useSelector(getUserInfo);
  const { TextArea } = Input;

  // helper to extract fileList from Upload event
  const getFile = (e) => (Array.isArray(e) ? e : e && e.fileList);

  // --------------------------------------------------------------------------
  // 1) PROFILE SECTION
  // --------------------------------------------------------------------------
  const handleProfileUpdate = (values) => {
    const payload = {
      aboutMe: values.about_me,
      skillIds: values.skills,
      languageIds: values.speaks,
      interestIds: values.interests,
    };
    updateGuideProfile(payload)
      .then(() => {
        trigger({
          message: "Profile updated. Once approved, changes will reflect on your public profile.",
          type: "success",
        });
        setIsFormDisabled(true);
        fetchGuideProfile();
      })
      .catch((err) => {
        console.error(err);
        trigger({ message: "Failed to update profile.", type: "error" });
      });
  };

  // --------------------------------------------------------------------------
  // 2) VIDEO & THUMBNAIL SECTION
  // --------------------------------------------------------------------------
  const handleVideoUpdate = async (values) => {
    const thumbFiles = values.introVidThumbUrl || [];
    const vidFiles = values.introVidUrl || [];

    // 2.a) no new files selected → just exit edit mode
    if (!thumbFiles.length && !vidFiles.length) {
      setIsVideoFormDisabled(true);
      return;
    }

    setIsVideoUpdating(true);

    try {
      // 2.b) thumbnail
      if (thumbFiles.length) {
        const formData = new FormData();
        formData.append("file", thumbFiles[0].originFileObj);
        if (thumbnailFileList[0]?.uid) {
          // existing → update
          await updateFile(thumbnailFileList[0].uid, formData);
          trigger({
            message: "Profile updated. Once approved, changes will reflect on your public profile.",
            type: "success",
          });
        } else {
          // new → create
          await uploadFile(7, formData);
          trigger({
            message:
              "Thumbnail uploaded. Once approved, changes will reflect on your public profile.",
            type: "success",
          });
        }
      }

      // 2.c) intro video
      if (vidFiles.length) {
        const formData = new FormData();
        formData.append("file", vidFiles[0].originFileObj);
        if (videoFileList[0]?.uid) {
          await updateFile(videoFileList[0].uid, formData);
          trigger({
            message:
              "Intro Profile updated. Once approved, changes will reflect on your public profile.",
            type: "success",
          });
        } else {
          await uploadFile(6, formData);
          trigger({
            message:
              "Intro video uploaded. Once approved, changes will reflect on your public profile.",
            type: "success",
          });
        }
      }

      // 2.d) done
      videoForm.resetFields();
      setIsVideoFormDisabled(true);
      fetchGuideProfile();
    } catch (err) {
      console.error(err);
      trigger({ message: "Failed to save video changes.", type: "error" });
    } finally {
      setIsVideoUpdating(false);
    }
  };

  // --------------------------------------------------------------------------
  // 3) FETCH GUIDE PROFILE & POPULATE FORMS/PREVIEWS
  // --------------------------------------------------------------------------
  const fetchGuideProfile = useCallback(() => {
    getGuideProfile()
      .then((res) => {
        setGuideProfileData(res);
        setIsProfileIncomplete(getMissingFields(res).length > 0);

        // thumbnail preview
        if (res.introVidThumbUrl) {
          setThumbnailFilelist([
            {
              uid: res.introVidThumbUrl.id,
              name: "Thumbnail Image",
              status: "done",
              url: res.introVidThumbUrl.signedUrl,
              thumbUrl: res.introVidThumbUrl.signedUrl,
            },
          ]);
        }

        // video preview
        if (res.introVidUrl) {
          setVideoFileList([
            {
              uid: res.introVidUrl.id,
              name: "Intro video",
              status: "done",
              url: res.introVidUrl.signedUrl,
              thumbUrl: res.introVidUrl.signedUrl,
            },
          ]);
        }

        // fill profile form
        form.setFieldsValue({
          country: res.country?.name || "",
          countryOfOrigin: res.countryOfOrigin?.name || "",
          university: res.university?.name || "",
          course: res.courseName || "",
          level: res.level?.name || "",
          discipline: res.discipline?.name || "",
          yearOfCompletion: res.yearOfCompletion
            ? dayjs(res.yearOfCompletion).format("DD-MM-YYYY")
            : "",
          skills: Array.isArray(res.skills) ? res.skills.map((x) => x.id) : [],
          speaks: Array.isArray(res.languages) ? res.languages.map((x) => x.id) : [],
          interests: Array.isArray(res.interests) ? res.interests.map((x) => x.id) : [],
          about_me: res.aboutMe || "",
        });
      })
      .catch(console.error);
  }, [form]);

  // --------------------------------------------------------------------------
  // 4) LOAD SKILLS, INTERESTS, LANGUAGES + PROFILE
  // --------------------------------------------------------------------------
  useEffect(() => {
    getAllSkills()
      .then((res) => setSkillsList(res.map((x) => ({ label: x.name, value: x.id }))))
      .catch(console.error);

    getAllInterests()
      .then((res) => setIntrestList(res.map((x) => ({ label: x.name, value: x.id }))))
      .catch(console.error);

    getAllLanguages()
      .then((res) => setLaunguageList(res.map((x) => ({ label: x.name, value: x.id }))))
      .catch(console.error);

    fetchGuideProfile();
  }, [fetchGuideProfile]);

  // set user name once fetched
  useEffect(() => {
    form.setFieldsValue({
      name: `${userInfo.user.firstName} ${userInfo.user.lastName}`,
    });
  }, [form, userInfo]);

  useUnsavedChanges(!isFormDisabled);

  // --------------------------------------------------------------------------
  // TAG RENDER FOR MULTI-SELECT
  // --------------------------------------------------------------------------
  const tagRender = ({ label, closable, onClose }) => {
    const block = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    return (
      <div
        className={isFormDisabled ? "disabled selected-chip" : "selected-chip"}
        onMouseDown={block}
      >
        <span>{label}</span>
        <IconCross
          style={{ cursor: isFormDisabled ? "not-allowed" : "pointer" }}
          color={isFormDisabled ? "#6d6e75" : "#db5800"}
          onClick={onClose}
        />
      </div>
    );
  };

  // --------------------------------------------------------------------------
  // COPY PROFILE LINK
  // --------------------------------------------------------------------------
  const handleCopyToClipboard = () => {
    const { firstName, lastName, country, id } = guideProfileData;
    const code = guideProfileData.country?.code;
    const path = `${window.location.origin}/guideProfile/${firstName}-${lastName}/${code}/${id}`;
    navigator.clipboard
      .writeText(path)
      .then(() => trigger({ type: "success", message: "Link copied to clipboard." }));
  };

  // --------------------------------------------------------------------------
  // POPOVER MENU FOR SINGLE-FILE UPDATES
  // --------------------------------------------------------------------------
  const popoverContent = () => (
    <Flex align="center" gap="0.5rem" vertical className="color-brand">
      <Upload
        fileList={[]}
        multiple={false}
        accept=".jpg,.jpeg,.png,.heic"
        maxCount={1}
        beforeUpload={(f) => f.size / 1024 / 1024 <= 5 || message.error("Max 5MB")}
        onChange={(e) => {
          const fd = new FormData();
          fd.append("file", e.fileList[0].originFileObj);
          updateFile(thumbnailFileList[0].uid, fd)
            .then(() => {
              fetchGuideProfile();
              trigger({
                type: "success",
                message:
                  "Profile updated. Once approved, changes will reflect on your public profile.",
              });
            })
            .catch(() => trigger({ type: "error", message: "Thumbnail update failed." }));
        }}
      >
        <ButtonComponent icon={<IconUploadV2 />}>Update Thumbnail</ButtonComponent>
      </Upload>

      <Upload
        fileList={[]}
        multiple={false}
        accept=".mp4,.mov,.avi,.mkv,.webm,.flv"
        maxCount={1}
        beforeUpload={(f) => f.size / 1024 / 1024 <= 100 || message.error("Max 100MB")}
        onChange={(e) => {
          const fd = new FormData();
          fd.append("file", e.fileList[0].originFileObj);
          updateFile(videoFileList[0].uid, fd)
            .then(() => {
              fetchGuideProfile();
              trigger({
                type: "success",
                message:
                  "Profile updated. Once approved, changes will reflect on your public profile.",
              });
            })
            .catch(() => trigger({ type: "error", message: "Video update failed." }));
        }}
      >
        <ButtonComponent icon={<IconUploadV2 />}>Update Video</ButtonComponent>
      </Upload>
    </Flex>
  );

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <Fragment>
      <Toaster />
      {element}
      <div className="guide-profile">
        <Container>
          <CardComponent>
            {/* HEADER */}
            <Flex justify="space-between" align="center" wrap style={{ gap: 10 }}>
              <BackWithLabel
                label={`${!isFormDisabled ? "Edit " : ""}Mentor Profile Details`}
                path="/profile"
              />
              {!isProfileIncomplete && (
                <IconShare onClick={handleCopyToClipboard} className="cursor-pointer" />
              )}
            </Flex>

            {/* PROFILE FORM */}
            <div className="profile-section">
              <Form
                form={form}
                name="profileForm"
                layout="vertical"
                onFinish={handleProfileUpdate}
                requiredMark={customizeRequiredMark}
                {...formItemLayoutWithOutLabel}
                onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
              >
                {/* first row */}
                <Row gutter={[16, 0]} className={isFormDisabled ? "" : "sideborder"}>
                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                    <Form.Item label="Name" name="name">
                      <Input
                        type="text"
                        disabled={true}
                        autoComplete="off"
                        placeholder="Enter your Name"
                      />
                    </Form.Item>
                  </Col>
                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                    <Form.Item label="Country" name="country">
                      <Input type="text" disabled={true} autoComplete="off" placeholder="Country" />
                    </Form.Item>
                  </Col>
                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                    <Form.Item label="Country of Origin" name="countryOfOrigin">
                      <Input
                        type="text"
                        disabled={true}
                        autoComplete="off"
                        placeholder="Country of Origin"
                      />
                    </Form.Item>
                  </Col>
                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                    <Form.Item
                      label="College/University (Current)"
                      name="university"
                      rules={[
                        {
                          required: true,
                          message: "Please enter your college/university!",
                        },
                      ]}
                    >
                      <Input
                        type="text"
                        disabled={true}
                        autoComplete="off"
                        placeholder="College/University"
                        onKeyPress={(e) => {
                          const charCode = e.which || e.keyCode;
                          if (!/[A-Za-z\s]/.test(String.fromCharCode(charCode))) {
                            e.preventDefault();
                          }
                        }}
                        onPaste={(e) => {
                          const pasteData = e.clipboardData.getData("text");
                          if (!/^[A-Za-z\s]+$/.test(pasteData)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                    <Form.Item label="Course" name="course">
                      <Input
                        disabled={true}
                        autoComplete="off"
                        placeholder="Course"
                        onKeyPress={(e) => {
                          const charCode = e.which || e.keyCode;
                          if (!/[A-Za-z\s]/.test(String.fromCharCode(charCode))) {
                            e.preventDefault();
                          }
                        }}
                        onPaste={(e) => {
                          const pasteData = e.clipboardData.getData("text");
                          if (!/^[A-Za-z\s]+$/.test(pasteData)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                    <Form.Item label="Level">
                      <Space.Compact style={{ width: "100%" }}>
                        <Form.Item name="level" noStyle>
                          <Input
                            type="text"
                            style={{
                              width: "100%",
                            }}
                            disabled={true}
                            placeholder="Level"
                          />
                        </Form.Item>
                      </Space.Compact>
                    </Form.Item>
                  </Col>
                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                    <Form.Item label="Discipline">
                      <Space.Compact style={{ width: "100%" }}>
                        <Form.Item name="discipline" noStyle>
                          <Input
                            type="text"
                            style={{
                              width: "100%",
                            }}
                            disabled={true}
                            placeholder="Discipline"
                          />
                        </Form.Item>
                      </Space.Compact>
                    </Form.Item>
                  </Col>
                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                    <Form.Item label="Year of Completion">
                      <Space.Compact style={{ width: "100%" }}>
                        <Form.Item name="yearOfCompletion" noStyle>
                          <Input
                            type="text"
                            style={{
                              width: "100%",
                            }}
                            disabled={true}
                            placeholder="Year of completion"
                          />
                        </Form.Item>
                      </Space.Compact>
                    </Form.Item>
                  </Col>
                </Row>

                {/* second row: only Skills, Speaks, Interests, About Me */}
                <Row gutter={[16, 0]} className={isFormDisabled ? "" : "sideborder"}>
                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                    <Form.Item
                      label="Skills"
                      name="skills"
                      rules={[
                        {
                          required: true,
                          message: "Please select at least one skill!",
                        },
                      ]}
                    >
                      <Select
                        virtual={false}
                        mode="multiple"
                        placeholder="Select a Skills"
                        className="w-100 multiSelect"
                        defaultBg="#fff5f0"
                        defaultColor="#DB5800"
                        tagRender={tagRender}
                        optionFilterProp="label"
                        options={skillsList}
                        disabled={isFormDisabled}
                      />
                    </Form.Item>
                  </Col>
                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                    <Form.Item
                      label="Speaks"
                      name="speaks"
                      rules={[
                        {
                          required: true,
                          message: "Please select at least one language!",
                        },
                      ]}
                    >
                      <Select
                        virtual={false}
                        mode="multiple"
                        placeholder="Select a Speaks"
                        className="w-100 multiSelect"
                        defaultBg="#fff5f0"
                        defaultColor="#DB5800"
                        tagRender={tagRender}
                        optionFilterProp="label"
                        options={launguageList}
                        disabled={isFormDisabled}
                      />
                    </Form.Item>
                  </Col>
                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                    <Form.Item
                      label="Interests"
                      name="interests"
                      rules={[
                        {
                          required: true,
                          message: "Please select at least one interest!",
                        },
                      ]}
                    >
                      <Select
                        virtual={false}
                        mode="multiple"
                        placeholder="Select a Interests"
                        className="w-100 multiSelect"
                        defaultBg="#fff5f0"
                        defaultColor="#DB5800"
                        tagRender={tagRender}
                        optionFilterProp="label"
                        options={intrestList}
                        disabled={isFormDisabled}
                      />
                    </Form.Item>
                  </Col>
                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                    <Form.Item
                      label="About Me"
                      name="about_me"
                      rules={[
                        {
                          required: true,
                          message: "Please tell us about yourself!",
                        },
                      ]}
                    >
                      <TextArea
                        autoSize={{ minRows: 5 }}
                        disabled={isFormDisabled}
                        style={{ padding: "10px" }}
                        placeholder="About me"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* Update Guide Profile / Save Changes button */}
                <Row justify="center" style={{ padding: "10px 0" }}>
                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                    <ButtonComponent
                      label={isFormDisabled ? "Update Guide Profile" : "Save Changes"}
                      varient="primary"
                      htmlType={"button"}
                      onClick={() => {
                        if (isFormDisabled) {
                          setIsFormDisabled(false);
                          e.preventDefault();
                          return;
                        }

                        form.submit();
                      }}
                      className="w-100"
                    />
                  </Col>
                </Row>
              </Form>
            </div>

            {/* VIDEO & THUMBNAIL SECTION */}
            <div className="video-section">
              <Form
                form={videoForm}
                name="videoForm"
                layout="vertical"
                onFinish={handleVideoUpdate}
                requiredMark={customizeRequiredMark}
                {...formItemLayoutWithOutLabel}
                onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
              >
                {!isVideoFormDisabled ? (
                  /* EDIT MODE: draggers for both fields */
                  <Row gutter={[16, 0]} className="sideborder">
                    <Col span={8}>
                      <Form.Item
                        name="introVidThumbUrl"
                        label="Thumbnail image"
                        getValueFromEvent={getFile}
                        rules={[
                          {
                            required: !thumbnailFileList.length,
                            message: "Select thumbnail",
                          },
                        ]}
                      >
                        <Dragger
                          maxCount={1}
                          customRequest={({ onSuccess }) => setTimeout(() => onSuccess("ok"), 0)}
                          beforeUpload={(file) =>
                            file.size / 1024 / 1024 <= 5 || message.error("Max size 5MB")
                          }
                          accept=".jpg,.jpeg,.png,.heic"
                        >
                          <p className="ant-upload-drag-icon">
                            <IconUploadDocs />
                          </p>
                          <p className="ant-upload-text">Tap to upload thumbnail image</p>
                        </Dragger>
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="introVidUrl"
                        label="Intro Video"
                        getValueFromEvent={getFile}
                        rules={[
                          {
                            required: !videoFileList.length,
                            message: "Select intro video",
                          },
                        ]}
                      >
                        <Dragger
                          maxCount={1}
                          customRequest={({ onSuccess }) => setTimeout(() => onSuccess("ok"), 0)}
                          beforeUpload={(file) =>
                            file.size / 1024 / 1024 <= 100 || message.error("Max size 100MB")
                          }
                          accept=".mp4,.mov,.avi,.mkv,.webm,.flv"
                        >
                          <p className="ant-upload-drag-icon">
                            <IconUploadDocs />
                          </p>
                          <p className="ant-upload-text">Tap to upload intro video</p>
                        </Dragger>
                      </Form.Item>
                    </Col>
                  </Row>
                ) : (
                  /* PREVIEW MODE */
                  <Row gutter={[16, 0]}>
                    <Col span={8}>
                      <Form.Item label="Intro Video">
                        <div className="w-100 image-preview cursor-pointer">
                          <img
                            src={thumbnailFileList[0]?.url}
                            style={{ width: "100%", height: "100%" }}
                            alt="thumb"
                          />
                          <div
                            className="play-btn"
                            onClick={() => openLinkInNewTab(videoFileList[0]?.url)}
                          >
                            <img src="/assets/images/playImage.png" alt="play" />
                          </div>
                          <div className="action-btn">
                            <Popover
                              content={popoverContent()}
                              trigger="hover"
                              placement="bottomRight"
                            >
                              <IconDots className="cursor-pointer" color="#fff" />
                            </Popover>
                          </div>
                        </div>
                      </Form.Item>
                    </Col>
                  </Row>
                )}

                <Row justify="center" style={{ padding: "10px 0" }}>
                  <Col span={8}>
                    <ButtonComponent
                      label={isVideoFormDisabled ? "Update Intro Video" : "Save Changes"}
                      variant="primary"
                      htmlType="button"
                      disabled={isVideoUpdating}
                      onClick={() =>
                        isVideoFormDisabled ? setIsVideoFormDisabled(false) : videoForm.submit()
                      }
                      className="w-100"
                    />
                  </Col>
                </Row>
              </Form>
            </div>
          </CardComponent>
        </Container>
      </div>
    </Fragment>
  );
};

export default GuideProfileDetail;
